import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DIALOG_DATA, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../auth.service';
import { ProductsService } from '../products/products.service';
import { ProductPreviewDetailsDialogComponent } from './product-preview-details-dialog.component';
import { ProductRow } from '../products/products.service';
import {
  CatalogProduct,
  CreationOptions,
  CreationPolicy,
  PackagingMode,
  PalletChoice,
  ProductCreateRequest,
  ProductCreateResponse
} from './product-creation.types';
import { ProductLabelDialogService } from './product-label-dialog.service';

const emptyDraft = () => ({
  model: '',
  code: '',
  serial: '',
  barcode: '',
  description: '',
  serialized: false,
  mode: 'unpackaged' as PackagingMode,
  packageName: '',
  quantity: 1,
  length: '',
  width: '',
  height: '',
  weight: '',
  po: '',
  so: '',
  available: false
});

@Component({
  selector: 'app-product-create-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule],
  templateUrl: './product-create-dialog.component.html',
  styleUrls: ['./product-create-dialog.component.scss']
})
export class ProductCreateDialogComponent implements OnInit, OnDestroy {
  @ViewChild('entry') entry?: NgForm;

  customers: CreationOptions['customers'] = [];
  customer = '';
  policy: CreationPolicy | null = null;
  draft = emptyDraft();
  catalogQuery = '';
  catalog: CatalogProduct[] = [];
  selectedCatalog: CatalogProduct | null = null;
  catalogTotal = 0;
  catalogMore = false;
  catalogLoading = false;
  catalogError = '';
  palletQuery = '';
  pallets: PalletChoice[] = [];
  selectedPallet: PalletChoice | null = null;
  palletLoading = false;
  palletError = '';
  loading = true;
  saving = false;
  error = '';
  available = false;
  uncertainSave = false;
  review: ProductCreateRequest | null = null;
  result: ProductCreateResponse['data'] | null = null;
  private printAfterSave = false;
  private saveKey = '';
  private savedAny = false;
  private destroyed = new Subject<void>();
  private customerRequests = new Subscription();
  private catalogRequest?: Subscription;
  private palletRequest?: Subscription;
  private catalogTimer?: ReturnType<typeof setTimeout>;
  private palletTimer?: ReturnType<typeof setTimeout>;

  readonly modeLabels: Record<PackagingMode, string> = {
    new_package: 'Create one package for this product',
    unpackaged: 'Leave this carton unpackaged',
    new_pallet: 'Create a new pallet and place this carton on it',
    existing_pallet: 'Place this carton on an existing unassigned pallet',
    package_only: 'Create a package with declared contents'
  };

  constructor(
    @Inject(DIALOG_DATA) public data: { customer: string; theme: 'dark' | 'light' },
    private readonly service: ProductsService,
    private readonly ref: DialogRef<boolean, ProductCreateDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly labels: ProductLabelDialogService,
    private readonly auth: AuthService
  ) {}

  ngOnInit(): void {
    this.service
      .creationOptions()
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (response) => {
          this.customers = response.customers;
          this.loading = false;
          if (this.restorePendingSave()) {
            return;
          }
          if (this.customers.some((c) => c.customer_contact_uuid === this.data.customer)) {
            this.customer = this.data.customer;
            this.customerChanged();
          }
        },
        error: (error) => {
          this.loading = false;
          this.error = error.error?.message || 'Creation options could not load.';
        }
      });
  }

  customerChanged(): void {
    this.cancelSearches();
    this.customerRequests.unsubscribe();
    this.customerRequests = new Subscription();
    this.policy = null;
    this.draft = emptyDraft();
    this.selectedCatalog = null;
    this.selectedPallet = null;
    this.catalog = [];
    this.pallets = [];
    this.catalogQuery = '';
    this.palletQuery = '';
    this.catalogTotal = 0;
    this.catalogError = '';
    this.palletError = '';
    this.error = '';
    this.review = null;
    if (!this.customer) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.customerRequests.add(
      this.service.creationOptions(this.customer).subscribe({
        next: (response) => {
          this.policy = response.policy;
          if (this.policy) {
            this.draft.mode = this.policy.default_packaging_mode;
          }
          this.loading = false;
          this.searchCatalog();
        },
        error: (error) => {
          this.loading = false;
          this.error = error.error?.message || 'The customer profile could not load.';
        }
      })
    );
  }

  get dialogTitle(): string {
    if (this.result) {
      return 'Saved successfully';
    }
    if (this.review) {
      return 'Review before saving';
    }
    return 'Add Product';
  }

  get saveButtonLabel(): string {
    if (this.saving) {
      return 'Saving…';
    }
    if (this.uncertainSave) {
      return 'Retry this save';
    }
    return 'Save product';
  }

  needs(field: string): boolean {
    return this.policy?.required_fields.includes(field) ?? false;
  }
  get serialRequired(): boolean {
    return (
      this.needs('product_serial_number') ||
      this.draft.serialized ||
      this.selectedCatalog?.serialized === true
    );
  }
  get isCarton(): boolean {
    return this.policy?.entry_type === 'carton';
  }
  get outcome(): string {
    switch (this.draft.mode) {
      case 'new_package':
        return 'One product and one linked package. A package label will be available.';
      case 'unpackaged':
        return 'One carton product, with no package. A label is unavailable until it is packaged.';
      case 'new_pallet':
        return 'One carton product and one new pallet package containing it.';
      case 'existing_pallet':
        return 'One new carton product added to the selected pallet. No new package.';
      case 'package_only':
        return `One package with ${this.draft.quantity} declared units. No individual product rows.`;
    }
  }

  catalogChanged(): void {
    clearTimeout(this.catalogTimer);
    this.catalogRequest?.unsubscribe();
    this.catalog = [];
    this.catalogTimer = setTimeout(() => this.searchCatalog(), 350);
  }

  private searchCatalog(): void {
    if (!this.policy) {
      return;
    }
    this.catalogRequest?.unsubscribe();
    this.catalogError = '';
    if (this.catalogQuery && this.catalogQuery.trim().length < 3) {
      return;
    }
    this.catalogLoading = true;
    this.catalogRequest = this.service
      .catalog(this.customer, this.catalogQuery.trim())
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (response) => {
          this.catalog = response.products;
          this.catalogTotal = response.total_products;
          this.catalogMore = response.more_matches;
          this.catalogLoading = false;
        },
        error: (error) => {
          this.catalogLoading = false;
          this.catalogError = error.error?.message || 'Catalog search failed. Try again.';
        }
      });
  }

  selectCatalog(item: CatalogProduct): void {
    this.selectedCatalog = item;
    this.draft.model = item.product_model_number || '';
    this.draft.description = item.product_name || item.product_description || '';
    this.draft.serialized = item.serialized === true;
    const dimensionFactor = item.measurement_unit === 2 ? 1 / 2.54 : 1;
    const weightFactor = item.weight_unit === 2 ? 2.2046226218 : 1;
    this.draft.length = this.measure(item.shipping_length, dimensionFactor);
    this.draft.width = this.measure(item.shipping_width, dimensionFactor);
    this.draft.height = this.measure(item.shipping_height, dimensionFactor);
    this.draft.weight = this.measure(item.shipping_weight, weightFactor);
    this.catalog = [];
  }

  private measure(value: number | null, factor: number): string {
    if (value === null || !(value > 0)) {
      return '';
    }
    const convertedValue = value * factor;
    const roundedValue = Number(convertedValue.toFixed(6));
    return String(roundedValue);
  }

  manualEntry(): void {
    this.selectedCatalog = null;
    this.draft.serialized = false;
  }

  packagingChanged(): void {
    this.selectedPallet = null;
    this.palletQuery = '';
    this.pallets = [];
    this.palletRequest?.unsubscribe();
  }

  palletChanged(): void {
    clearTimeout(this.palletTimer);
    this.palletRequest?.unsubscribe();
    this.selectedPallet = null;
    this.pallets = [];
    this.palletError = '';
    if (this.palletQuery.trim().length < 3) {
      return;
    }
    this.palletTimer = setTimeout(() => {
      this.palletLoading = true;
      this.palletRequest = this.service
        .pallets(this.customer, this.palletQuery.trim())
        .pipe(takeUntil(this.destroyed))
        .subscribe({
          next: (response) => {
            this.pallets = response.pallets;
            this.palletLoading = false;
          },
          error: (error) => {
            this.palletError = error.error?.message || 'Pallet search failed.';
            this.palletLoading = false;
          }
        });
    }, 350);
  }

  selectPallet(pallet: PalletChoice): void {
    this.selectedPallet = pallet;
    this.pallets = [];
  }

  get canReview(): boolean {
    return (
      !!this.policy &&
      !!this.entry?.valid &&
      !this.loading &&
      (this.draft.mode !== 'existing_pallet' || !!this.selectedPallet)
    );
  }

  prepareReview(): void {
    if (!this.policy || !this.canReview) {
      return;
    }
    this.error = '';
    if (
      (this.policy.catalog_required || !this.policy.manual_entry_allowed) &&
      !this.selectedCatalog
    ) {
      this.error = 'Select a catalog item.';
      return;
    }
    if (this.draft.mode === 'existing_pallet' && !this.selectedPallet) {
      this.error = 'Select a pallet from the search results.';
      return;
    }
    this.review = {
      customer_contact_uuid: this.customer,
      profile_revision: this.policy.profile_revision,
      customer_product_uuid: this.selectedCatalog?.customer_product_uuid || null,
      product_model_number: this.draft.model.trim(),
      product_code: this.draft.code.trim(),
      product_serial_number: this.draft.serial.trim(),
      barcode: this.draft.barcode.trim(),
      description: this.draft.description.trim(),
      serialized: this.draft.serialized,
      packaging_mode: this.draft.mode,
      package_uuid: this.selectedPallet?.package_uuid || null,
      package_name: this.draft.packageName.trim(),
      declared_quantity: this.draft.mode === 'package_only' ? this.draft.quantity : 1,
      length: this.draft.length ? String(this.draft.length) : null,
      width: this.draft.width ? String(this.draft.width) : null,
      height: this.draft.height ? String(this.draft.height) : null,
      weight: this.draft.weight ? String(this.draft.weight) : null,
      po_number: this.draft.po.trim(),
      so_number: this.draft.so.trim(),
      available: this.draft.available
    };
    this.saveKey = crypto.randomUUID();
  }

  get canPrintCreatedPackage(): boolean {
    return !!this.review && this.review.packaging_mode !== 'unpackaged';
  }

  save(printLabel = false): void {
    if (!this.review || this.saving) {
      return;
    }
    if (!this.uncertainSave) {
      this.printAfterSave = printLabel;
    }
    this.saving = true;
    this.error = '';
    try {
      localStorage.setItem(
        this.pendingStorageKey,
        JSON.stringify({ body: this.review, key: this.saveKey, printLabel: this.printAfterSave })
      );
    } catch {
      this.saving = false;
      this.error = 'Enable browser storage so an interrupted save can be recovered safely.';
      return;
    }
    console.log(this.review);
    this.service
      .create(this.review, this.saveKey)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (response) => {
          this.clearPendingSave();
          this.result = response.data;
          this.savedAny = true;
          this.saving = false;
          this.uncertainSave = false;
          if (this.printAfterSave && this.result.package_uuid) {
            this.showLabel();
          }
          this.printAfterSave = false;
        },
        error: (error) => {
          this.saving = false;
          this.uncertainSave = !error.status || error.status >= 500;
          if (!this.uncertainSave) {
            this.clearPendingSave();
          }
          this.error =
            error.error?.message || 'The save could not be confirmed. Retry this same save.';
          console.error(error);
        }
      });
  }

  edit(): void {
    if (!this.saving && !this.uncertainSave) {
      this.review = null;
      this.error = '';
    }
  }
  another(): void {
    this.result = null;
    this.customerChanged();
  }
  close(): void {
    if (!this.saving && !this.uncertainSave) {
      this.ref.close(this.savedAny);
    }
  }

  showRecord(): void {
    if (!this.result) {
      return;
    }
    const row = {
      record_kind: this.result.product_uuid ? 'product' : 'package',
      record_uuid: this.result.product_uuid || this.result.package_uuid,
      product_uuid: this.result.product_uuid,
      package_uuid: this.result.package_uuid
    } as ProductRow;
    this.dialog.open(ProductPreviewDetailsDialogComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: { row, theme: this.data.theme }
    });
  }

  showLabel(): void {
    if (!this.result?.package_uuid) {
      return;
    }
    this.labels.open(this.result.package_uuid, this.data.theme);
  }

  private clearPendingSave(): void {
    try {
      localStorage.removeItem(this.pendingStorageKey);
    } catch {
      // The same saved request can still be replayed safely on the next visit.
    }
  }

  private get pendingStorageKey(): string {
    return 'products-pending-save:' + (this.auth.getCurrentUserSnapshot()?.email || 'unknown');
  }

  private restorePendingSave(): boolean {
    try {
      const pending = JSON.parse(localStorage.getItem(this.pendingStorageKey) || 'null') as {
        body: ProductCreateRequest;
        key: string;
        printLabel?: boolean;
      } | null;
      if (!pending?.body || !pending.key) {
        return false;
      }
      this.review = pending.body;
      this.saveKey = pending.key;
      this.printAfterSave = pending.printLabel === true;
      this.customer = pending.body.customer_contact_uuid;
      this.draft.mode = pending.body.packaging_mode;
      this.draft.quantity = pending.body.declared_quantity;
      this.uncertainSave = true;
      this.error = 'An earlier save needs confirmation. Retry it to retrieve its result safely.';
      this.customerRequests.add(
        this.service.creationOptions(this.customer).subscribe({
          next: (response) => {
            this.policy = response.policy;
          },
          error: () => {
            /* A completed save can replay even if its profile changed. */
          }
        })
      );
      return true;
    } catch {
      this.clearPendingSave();
      return false;
    }
  }

  private cancelSearches(): void {
    clearTimeout(this.catalogTimer);
    clearTimeout(this.palletTimer);
    this.catalogRequest?.unsubscribe();
    this.palletRequest?.unsubscribe();
    this.catalogLoading = false;
    this.palletLoading = false;
  }

  ngOnDestroy(): void {
    this.cancelSearches();
    this.customerRequests.unsubscribe();
    this.destroyed.next();
    this.destroyed.complete();
  }
}
